import algoliasearch from 'algoliasearch'
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch'
import instantsearch from 'instantsearch.js'
import { configure } from 'instantsearch.js/es/widgets'
import { applyFilters } from '@wordpress/hooks';

/**
 * Phase 1c (PLAN.md §2.2 / §2.4): de-Algolia the frontend globals/events/hook names, with
 * back-compat aliases kept for one major version (PLAN.md §4.4).
 *
 * Canonical global: window.tangibleSearchsyncInstances.
 * Back-compat alias: window.tangibleAlgoliaInstances === window.tangibleSearchsyncInstances
 * (same object reference, so anything reading/writing either name stays in sync).
 */
if (!window.tangibleSearchsyncInstances) window.tangibleSearchsyncInstances = {};
// Legacy alias: same object reference, not a copy, so both names always agree.
window.tangibleAlgoliaInstances = window.tangibleSearchsyncInstances;

/**
 * Minimal fetch-based InstantSearch searchClient for the Local backend (Phase 3b, PLAN.md
 * §2.2/§2.4): POSTs the InstantSearch multi-query request array as-is to
 * client_config.endpoint (Tangible\SearchSync\Local\Local_Backend::client_config(), see
 * src/Local/Local_Backend.php) and returns the parsed JSON body, which is already shaped as
 * `{ results: [...] }` by src/Local/Rest/Search_Controller.php — no translation needed in
 * either direction, unlike the Algolia/Meilisearch branches which call into a client SDK.
 *
 * X-WP-Nonce is sent on every request (client_config.nonce = wp_create_nonce('wp_rest')) so a
 * logged-in visitor's session flows through to the server (WordPress's REST nonce middleware
 * populates the current user from it) — nothing server-side reads the resulting user identity
 * yet (Local_Backend's route is fully public, see Search_Endpoint::permission_callback()), but
 * threading the nonce through now means Phase 4's per-user filters need no client-side change.
 *
 * Highlighting: InstantSearch's `_highlightResult`/`_snippetResult` fields are NOT synthesized
 * here, client- or server-side. Checked both shipped hit templates
 * (assets/src/hits-templates/default-template.js, course-template.js) and every frontend
 * widget file: none reference `_highlightResult`, `_snippetResult`, or InstantSearch's
 * `<Highlight>`/`<Snippet>` components — this plugin's UI has never used Algolia/Meilisearch
 * highlighting either, so there is nothing today that would consume a synthesized field.
 * Documented here (see also docs/frontend-architecture.md's Local client section) rather than
 * adding dead payload weight to every response; if a future widget/template starts reading
 * `_highlightResult`, the simpler of the two options (client-side, wrapping the matched query
 * terms in `<mark>` against each hit's display fields) should be implemented in this file at
 * that point, since it needs no server round-trip or REST contract change.
 */
const createLocalSearchClient = (clientConfig) => {
  const endpoint = clientConfig.endpoint;
  const nonce = clientConfig.nonce;

  return {
    search(requests) {
      return fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
        },
        body: JSON.stringify({ requests }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`SearchSync local search request failed with status ${response.status}`);
        }
        return response.json();
      });
    },
  };
};

/**
 * Search-client factory (Phase 2b/3b, PLAN.md §2.2): builds the InstantSearch searchClient for
 * whichever backend is active on the PHP side, from the data exposed by
 * includes/enqueue/class-algolia-component-enqueuer.php ('backend' + 'client_config').
 *
 * Everything downstream — the instantsearch() instance, widgets, routing — is unchanged and
 * backend-agnostic: only the searchClient construction varies.
 *
 * @param {object} tgblAlgoliaData Decoded tgblSearchsyncDataEncoded (aka legacy
 *   tgblAlgoliaDataEncoded) payload.
 * @returns {object} An InstantSearch-compatible searchClient.
 */
const createSearchClient = (tgblAlgoliaData) => {
  const backend = tgblAlgoliaData.backend;
  const clientConfig = tgblAlgoliaData.client_config || {};

  if (backend === 'meilisearch') {
    return instantMeiliSearch(
      clientConfig.host,
      clientConfig.search_key
    ).searchClient;
  }

  if (backend === 'local') {
    return createLocalSearchClient(clientConfig);
  }

  // Default / 'algolia' / no backend declared yet (back-compat with payloads that only ever
  // carried the legacy app_id/search_key keys, e.g. before this plugin registered any other
  // backend): fall back to the legacy top-level keys so nothing already deployed breaks.
  return algoliasearch(
    clientConfig.app_id || tgblAlgoliaData.app_id,
    clientConfig.search_key || tgblAlgoliaData.search_key
  );
}

//TODO:
// Test what happens when this tries to create a index that doesn't exist so we can handle the error
const getInstance = (indexId) => {
  if ( window.tangibleSearchsyncInstances[indexId] ) {
    return window.tangibleSearchsyncInstances[indexId]
  }

  // tgblSearchsyncDataEncoded is the payload global emitted by
  // includes/enqueue/class-algolia-component-enqueuer.php (canonical name; a legacy
  // tgblAlgoliaDataEncoded alias is emitted alongside it — see that file's enqueue_component()).
  const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded);
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);

  const searchClient = createSearchClient(tgblAlgoliaData)

  // Forward WordPress ?s= query as the initial search term, so a theme search form that
  // submits to the normal WP search URL (?s=...) lands the user on a page with this
  // dashboard already showing results for that query, rather than an empty search box.
  const wpQuery = new URLSearchParams(window.location.search).get('s') ?? '';

  const searchInstance = instantsearch({
    indexName: indexId,
    searchClient,
    searchParameters: {
      restrictSearchableAttributes: ['post_title'],
    },
    routing: true,
    initialUiState: wpQuery ? { [indexId]: { query: wpQuery } } : undefined,
  });

  searchInstance.addWidgets([
    configure({}),
  ])

  window.tangibleSearchsyncInstances[indexId] = searchInstance;
  return window.tangibleSearchsyncInstances[indexId]
}

const registerWidget = element => {

  element.setAttribute('data-init', "true")

  /**
   * If all the elements are registered we init the instances
   */
  const elements = document.querySelectorAll("[data-init]")

  let hasUninitialized = false
  elements.forEach(element => {
    if(element.getAttribute('data-init') === 'true') return;
    hasUninitialized = true
  })

  if(hasUninitialized === true) return

  for(const key in window.tangibleSearchsyncInstances) {
    window.tangibleSearchsyncInstances[key].start()
  }

  // After start, collect the preselection plan from filters and apply it.
  // wp-hooks name: canonical 'searchsync.filters.preselect', with the legacy
  // 'tangible-algolia-preselect-refinement' name applied first (same alias pattern as the PHP
  // hook aliases in includes/algolia/ — legacy runs first, canonical runs second so it can
  // override the legacy filter's result).
  if (!window.tangibleSearchsyncPreselectApplied) {
    let preselectionPlan = applyFilters('tangible-algolia-preselect-refinement', []);
    preselectionPlan = applyFilters('searchsync.filters.preselect', preselectionPlan);
    // preselectionPlan: [{ indexName, attribute, values: [] }, ...] for refinement-list-shaped
    // widgets, or [{ indexName, attribute, value, widgetType: 'menu' | 'menu_select' }, ...]
    // for single-select menu widgets (see searchsync-menu-component.js /
    // searchsync-menu-select-component.js, which register the latter shape).

    preselectionPlan.forEach(({ indexName, attribute, values, value, widgetType }) => {
      const search = window.tangibleSearchsyncInstances[indexName];
      if (!search || !search.helper) return;

      if (widgetType === 'menu' || widgetType === 'menu_select') {
        if (value) {
          search.renderState[indexName].menu[attribute].refine(value);
          search.helper.search();
        }
      } else {
        if (!Array.isArray(values)) return;

        // Use helper API: robust and version-stable
        values.forEach((preselectedRefinement) => {
          search.renderState[indexName].refinementList[attribute].refine(preselectedRefinement);
        });
        // Trigger a search after applying refinements for this index
        search.helper.search();
      }
    });

    window.tangibleSearchsyncPreselectApplied = true;
    // Legacy alias flag, kept in sync in case any external code reads it directly.
    window.tangibleAlgoliaPreselectApplied = true;
  }

  // Event for other consumers: canonical 'searchsyncInstanceCreated', plus legacy
  // 'algoliaInstanceCreated' dispatched for back-compat (one major version — PLAN.md §4.4).
  window.dispatchEvent(new CustomEvent('searchsyncInstanceCreated', {}));
  window.dispatchEvent(new CustomEvent('algoliaInstanceCreated', {}));
};

export {getInstance, registerWidget, createSearchClient, createLocalSearchClient}
